import { useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type CacheAnalyticsCallback,
	type DegradationCallback,
	get3pSearchConfig,
	getSearchConfigV2,
	getUserLoomAuthStatus,
	type LinkedSite,
	searchLinkedSitesQuery,
	type T3pResource,
	type TSearch3pConfigProps,
	type TSearchConfigAPIResponse,
} from '@atlassian/search-client';

import { type ProductKeys3P, ThirdPartyConfigs } from '../../common/constants/3p-product-configs';
import { EVENT_SOURCE } from '../../common/constants/analytics';
import { get3PLogoFromURL } from '../../common/constants/icons';
import { type NounKeys, nouns as supportedNouns } from '../../common/constants/nouns';
import {
	allProductKeys,
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../common/constants/products';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	type ForgeConnectorProductKey,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	LITE_CONNECTOR_SOURCE,
	type PlatformIntegrationAri,
	registerForgeConnectorIcon,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/constants/schemas/3p-config';
import { SearchExperience, type SearchStoreExperience } from '../../common/constants/search';
import { mapAtisToTypeFilterConfig } from '../../common/utils/ati-to-filter-type/map-atis-to-type-filter-config';
import { onDegradedDependency } from '../../common/utils/events/degraded-dependency';
import { onFailedDependency } from '../../common/utils/events/failed-dependency';
import { onRequestCacheAccess } from '../../common/utils/events/on-cache-access';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../../common/utils/events/vital-stats-events';
import { registerForgeConnectorTypeMapping } from '../../common/utils/filters/constants';
import { createVitalStatsNetworkCallbacks } from '../../common/utils/network-task-tracker';
import { useAnalytics } from '../analytics';
import { useLogException } from '../error-boundary/utils';
import { useSearchSessionId } from '../store/bootstrap';

const EXTRACT_PRODUCT = /ari:cloud:(.*)?::/;
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;
const BITBUCKET_WORKSPACE_ARI = 'ari:cloud:bitbucket::workspace';

export type UseFetchConfigProps = {
	thirdPartyAbsoluteUrl?: string;
	thirdPartyDisabled?: boolean;
	userId?: string | null;
	cloudId?: string;
	aggAbsoluteUrl?: string;
	primaryProduct?: PrimaryProductKey;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration i.e. list of products accessible by the logged in user.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * No need to pass if the UI is being bootstrapped inside Jira, Confluence or any product tied to these sites.
	 */
	configFetchAbsoluteUrl?: string;
	aggregatorHostName?: string;
	multiSiteSearchInRovo?: boolean;
	experience?: SearchStoreExperience;
};

const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse) => {
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

const isNounsAvailable = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isNounsAvailable;
};

const isRovoEnabled = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isRovoEnabled;
};

const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

const getProductEdition = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

const getProductDataRegions = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

/**
 * Some products (eg. Loom) have separate auth checks that need to be enforced before
 * returning the final list of accessible products.
 *
 * This should only be used as a temporary solution, all auth checks should ideally be done
 * inside 1P and 3P configuration ednpoints.
 *
 * Remove when cleaning up fg('rovo-search-combined-loom-auth')
 */
const lastMileProductChecks = (isUserLoomAuthed: boolean, products?: ProductKeys[]) => {
	if (!products || products.length === 0) {
		return products;
	}

	return products.filter((product) => isUserLoomAuthed || product !== ProductKeys.Loom);
};

const getRovoFeatures = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.rovoFeatures,
	)?.rovoFeatures;
};

/**
 * A React hook which handles fetching of all the config necessary to bootstrap 1P + 3P results.
 */
export const useFetchConfig = ({
	cloudId: cloudIdProp,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	thirdPartyAbsoluteUrl,
	thirdPartyDisabled,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	experience,
}: UseFetchConfigProps) => {
	const supportedProducts = useProductConfigs();
	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);

	// Loom requires a separate auth check, which is stored separately to `accessibleProductsConfig`.
	// This means Loom can be accessible through `accessibleProductsConfig`,
	// but unavailable to the user because of the auth check.
	// Clean up when removing fg('rovo-search-combined-loom-auth')
	const [isUserLoomAuthed, setIsUserLoomAuthed] = useState<boolean>(false);

	// Rovo Multiple Site Search
	const [linkedCloudARIs, setLinkedCloudARIs] = useState<string[] | undefined>(undefined);

	const [accessibleProductsConfig, setAccessibleProductsConfig] = useState<{
		products?: ProductKeys[];
		nouns: NounKeys[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({
		nouns: [],
	});
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	const [searchSessionId] = useSearchSessionId();

	const logException = useLogException();

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	const thirdPartyResponse = useThirdPartyConfig({
		cloudId: cloudIdProp || firstCloudId || '',
		userId: userId,
		absoluteUrl: thirdPartyAbsoluteUrl,
		disabled: thirdPartyDisabled,
		experience,
	});

	// Rovo Multiple Site Search - Store linked site ARIs in state
	useEffect(() => {
		const cloudId = cloudIdProp || firstCloudId;
		if (cloudId && multiSiteSearchInRovo) {
			searchLinkedSitesQuery({
				variables: { id: `ari:cloud:confluence::site/${cloudId}` },
				aggAbsoluteUrl,
			})
				.then((response) => {
					const edges = response?.data?.collabContext_workspaceIsConnectedToWorkspace?.edges ?? [];
					const linkedARIs = edges.map((edge: LinkedSite) => edge.node.id);

					// Move current cloudId to index 0 if it exists and isn't already there
					// This is a temporary solution for multiple site search in rovo with correct CloudIdARIs sequence
					const currentCloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
					const currentIndex = linkedARIs.findIndex((ari) => ari === currentCloudIdARI);

					if (currentIndex > 0) {
						// Remove from current position and add to beginning
						linkedARIs.splice(currentIndex, 1);
						linkedARIs.unshift(currentCloudIdARI);
					}

					setLinkedCloudARIs(linkedARIs);
				})
				.catch((error) => {
					logException(error);
				});
		}
	}, [cloudIdProp, firstCloudId, multiSiteSearchInRovo, logException, aggAbsoluteUrl]);

	useEffect(() => {
		// this endpoint is working in confluence, atlas, jira and focus
		// check auth in above products, and directly show loom to other products for now
		// Remoe when cleaning up fg('rovo-search-combined-loom-auth')
		if (!fg('rovo-search-combined-loom-auth') && !isUserLoomAuthed) {
			if (
				primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Atlas ||
				((primaryProduct === PrimaryProductKeys.Jira ||
					primaryProduct === PrimaryProductKeys.Focus) &&
					fg('search_loom_auth_check_in_jira_mercury'))
			) {
				getUserLoomAuthStatus({
					userId,
					cloudId: cloudIdProp,
				})
					.then((response) => setIsUserLoomAuthed(response?.status === 'mastered'))
					.catch((error) => {
						logException(error);

						setIsUserLoomAuthed(false);
					});
			} else {
				setIsUserLoomAuthed(true);
			}
		}
	}, [isUserLoomAuthed, setIsUserLoomAuthed, userId, logException, cloudIdProp, primaryProduct]);

	useEffect(() => {
		if (!firstPartyResponse) {
			const uxTaskId = crypto.randomUUID();
			const taskStartAt = performance.now();

			const baseUXVitalStatsParams = {
				searchSessionId,
				taskId: uxTaskId,
				task: TASK_NAMES.SEARCH_PAGE_FIRST_PARTY_CONFIG_UX,
			};

			// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
			const degradationCallback: DegradationCallback = (reason) => {
				fireAnalyticsEvent(
					onDegradedDependency({
						dependency: 'search-config',
						searchSessionId,
						reason,
						source: EVENT_SOURCE,
					}),
				);
			};

			const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
				fireAnalyticsEvent(
					onRequestCacheAccess({
						requestName: 'search-config',
						searchSessionId,
						cacheHit: state.hit,
						refreshTriggered: state.refreshTriggered,
						source: EVENT_SOURCE,
					}),
				);
			};

			/**
			 * These parameters define a caching strategy where:
			 *  - Cache is refreshed only if the cached data is expired, with a 15 minutes TTL
			 *  - Cached value is returned, regardless of whether its expired or not
			 */
			const search1PConfigTTL = fg('rovo_search_1p_config_increase_ttl')
				? 1000 * 60 * 15
				: 1000 * 60 * 1; // 15 minutes : 1 minute

			// Create network callbacks for tracking network requests
			const configNetworkTaskId = crypto.randomUUID();
			const networkCallbacks = createVitalStatsNetworkCallbacks(
				configNetworkTaskId,
				TASK_NAMES.SEARCH_PAGE_FIRST_PARTY_CONFIG_NETWORK,
				searchSessionId,
				fireAnalyticsEvent,
				taskStartAt,
				{ uxTaskId },
			);

			const searchConfigParams = {
				cloudId: cloudIdProp,
				absoluteUrl: configFetchAbsoluteUrl,
				hostName: aggregatorHostName,
				userId,
				degradationCallback,
				cacheAnalyticsCallback,
				expiration: search1PConfigTTL, // 15 minutes
				returnExpired: true,
				onlyRefreshIfExpired: true,
				networkCallbacks,
			};

			// Fire task start for UX tracking
			fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));

			getSearchConfigV2(searchConfigParams)
				.then((response) => {
					setFirstPartyResponse(response);

					// Fire user experience metric - user got data (success from UX perspective)
					fireAnalyticsEvent(
						onTaskSuccess({
							...baseUXVitalStatsParams,
							moreContext: {
								timeTaken: performance.now() - taskStartAt,
							},
						}),
					);
				})
				.catch((error) => {
					logException(error);

					// FailedDependency is visible to the end-user. This is a health metric for our UX
					fireAnalyticsEvent(
						onFailedDependency({
							dependency: 'search-config',
							searchSessionId,
							source: EVENT_SOURCE,
						}),
					);

					// Fire user experience metric - user did not get data (failure from UX perspective)
					fireAnalyticsEvent(
						onTaskFail({
							...baseUXVitalStatsParams,
							moreContext: {
								error: error.name,
								timeTaken: performance.now() - taskStartAt,
							},
						}),
					);

					setFirstPartyResponse({
						resources: [],
						intercomHmac: '',
						siteMetadata: [],
					});
				});
		}
	}, [
		firstPartyResponse,
		setFirstPartyResponse,
		cloudIdProp,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
	]);

	useEffect(() => {
		if (
			thirdPartyResponse.productKeys &&
			firstPartyResponse &&
			!accessibleProductsConfig.products
		) {
			let allAccessibleProducts: ProductKeys[] = [];

			const firstPartyProducts = [
				...new Set(
					firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedProducts) {
							return [match[1]];
						}

						return [];
					}),
				),
			];

			if (firstPartyProducts.length === 0 && primaryProduct) {
				// Use the primary product if the 1P Search Config request fails
				allAccessibleProducts.push(primaryProduct as ProductKeys);
			} else {
				allAccessibleProducts.push(...(firstPartyProducts as ProductKeys[]));
			}

			// We are currently migrating Goal and Project nouns from townsquare (Atlas) into their own separate Products
			// within Identity. As part of this migration we are decommissioning the isNounsAvailable flag on siteMetadata
			// and moving the nouns into the first party product list.
			//
			// If a site has isNounsAvailable set to false then we want to return the available
			// nouns from the firstPartyResponse. If isNounsAvailable is true then the site hasn't been migrated yet and
			// should continue with the old behaviour.
			const areNounsEnabledThroughLegacyFlag = isNounsAvailable(firstPartyResponse, cloudIdProp);

			const accessibleNouns = areNounsEnabledThroughLegacyFlag
				? []
				: firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedNouns) {
							return [match[1] as NounKeys];
						}

						return [];
					});

			// If nouns are available we also need to add Townsquare to the list of accessible products.
			// This way Projects will be fetched by default when no filters are selected.
			if (accessibleNouns.length > 0 && !areNounsEnabledThroughLegacyFlag) {
				allAccessibleProducts.push(ProductKeys.Atlas);
			}

			// Remove Bitbucket from the list of resources if:
			// * The user doesn't have access to the Bitbucket product
			// * There are no bitbucket workspaces
			// See https://hello.atlassian.net/wiki/x/1hI6CQE for details
			if (
				allAccessibleProducts.includes('bitbucket') &&
				!firstPartyResponse.resources.find((r) => r.id.includes(BITBUCKET_WORKSPACE_ARI))
			) {
				allAccessibleProducts = allAccessibleProducts.filter(
					(product) => product !== ProductKeys.Bitbucket,
				);
			}

			// Allow 3P products only if a user has Rovo enabled.
			// Long-term it won't be needed if the 3p-configuration check ensures Rovo eligibility.
			// See https://hello.atlassian.net/wiki/spaces/SEARCH/pages/4469899124.
			const doesUserHaveRovo = isRovoEnabled(firstPartyResponse, cloudIdProp);
			if (doesUserHaveRovo) {
				const thirdPartyProductKeys = thirdPartyResponse.productKeys || [];
				allAccessibleProducts.push(...(thirdPartyProductKeys as ProductKeys[]));
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: thirdPartyResponse.thirdPartyConfigs,
				});
			} else {
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: {},
				});
			}
			setIsConfigLoaded(true);
		}
	}, [
		isUserLoomAuthed,
		thirdPartyResponse,
		firstPartyResponse,
		setAccessibleProductsConfig,
		primaryProduct,
		fireAnalyticsEvent,
		logException,
		cloudIdProp,
		accessibleProductsConfig.products,
		supportedProducts,
	]);

	const accessibleProducts = fg('rovo-search-combined-loom-auth')
		? accessibleProductsConfig.products
		: lastMileProductChecks(isUserLoomAuthed, accessibleProductsConfig.products);

	return {
		accessibleProducts,
		accessibleNouns: accessibleProductsConfig.nouns,
		thirdPartyConfigs: accessibleProductsConfig.thirdPartyConfigs,
		firstCloudId,
		intercomHmac: firstPartyResponse?.intercomHmac,
		isNounsAvailable: isNounsAvailable(firstPartyResponse, cloudIdProp),
		isRovoEnabled: isRovoEnabled(firstPartyResponse, cloudIdProp),
		isJiraIssueIndexed: getJiraIssueIndexedForTenant(firstPartyResponse, cloudIdProp),
		productEdition: getProductEdition(firstPartyResponse, cloudIdProp),
		productDataRegions: getProductDataRegions(firstPartyResponse, cloudIdProp),
		rovoFeatures: getRovoFeatures(firstPartyResponse, cloudIdProp),
		isConfigLoaded,
		linkedCloudARIs,
	};
};

export const getThirdPartyTypes = (): Record<string, string> => {
	// old sharepoint product key is put here (kept for backwards compatibility), as the new one is in the config
	const oldThirdPartyTypes: Record<string, string> = {
		'ai-3p-connector:sharepoint-domain': `sharepoint`,
		'ai-3p-connector:google-workspace': `drive`,
	};

	const thirdPartyTypes: Record<string, string> = {
		...oldThirdPartyTypes,
		// add third party types for dual connectors
		'lite-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive': `drive`,
		'smartlinks-connector-gdrive-rsl': `drive`, // needed for relevance testing
		'smartlinks-connector-salesforce': `salesforce`,
		'smartlinks-connector-airtable': `airtable`,
		'smartlinks-connector-figma': `figma`,
		'smartlinks-connector-gitlab': `gitlab`,
		'smartlinks-connector-miro': `miro`,
		'smartlinks-connector-asana': `asana`,
		'smartlinks-connector-smartsheet': `smartsheet`,
		'smartlinks-connector-dropbox': `dropbox`,
		'smartlinks-connector-box': `box`,
		'smartlinks-connector-lucid': `lucid`,
		'smartlinks-connector-hubspot': `hubspot`,
		'smartlinks-connector-docusign': `docusign`,
		'smartlinks-connector-slack': `slack`,
		'smartlinks-connector-teams': `teams`,
		'smartlinks-connector-azure-devops': `azure-devops`,
		'smartlinks-connector-notion': `notion`,
		'smartlinks-connector-onedrive': `onedrive`,
		'smartlinks-connector-sharepoint': `sharepoint`,
	};

	ThirdPartyConfigs.forEach((config) => {
		if (config.resourceType) {
			thirdPartyTypes[config.resourceType] = config.id;
		}
	});

	return thirdPartyTypes;
};

type ConnectorSourceMap = Map<string, Set<ConnectorSource>>;

const mapForgeConnectorToThirdPartyConfig = (
	forgeConnector: T3pResource,
): ThirdPartyConfigsWithAPIResponse | undefined => {
	// Validate required fields
	if (!forgeConnector.lightIcon || !forgeConnector.workspaceName) {
		return undefined;
	}

	const icon = get3PLogoFromURL(forgeConnector.lightIcon, forgeConnector.workspaceName, 'medium');
	const searchResultIcon = get3PLogoFromURL(
		forgeConnector.lightIcon,
		forgeConnector.providerId,
		'large',
	);

	const productKey = forgeConnector.type as ForgeConnectorProductKey;

	// Register the icon in the registry so ProductOrNounIcon can access it without props
	registerForgeConnectorIcon(productKey, icon);

	// Build type filter config
	const typeFilterConfig = forgeConnector.entityTypes
		? mapAtisToTypeFilterConfig(forgeConnector.entityTypes)
		: new Map();

	// Register the type mapping in the cache so universal type filter can use it
	if (fg('rovo-search-dynamic-forge-config')) {
		registerForgeConnectorTypeMapping(productKey, typeFilterConfig);
	}

	const config: ThirdPartyConfigsWithAPIResponse = {
		id: forgeConnector.type,
		displayName: forgeConnector.environmentType
			? `${forgeConnector.workspaceName} (${forgeConnector.environmentType})`
			: forgeConnector.workspaceName,
		resourceType: forgeConnector.type,
		datasourceId: forgeConnector.datasourceId,
		connectionId: forgeConnector.connectionId,
		providerId: forgeConnector.providerId,
		outboundAuthUrl: forgeConnector.outboundAuthUrl,
		integrationAri: forgeConnector.integrationId
			? (forgeConnector.integrationId as unknown as PlatformIntegrationAri)
			: (`ari:cloud:platform::integration/${forgeConnector.providerId}` as PlatformIntegrationAri),
		filters: ['lastUpdated', 'universalTypeFilter'],
		displayWorkspaceNameAsProductName: true,
		isEnabled: () => true,
		userNeedsOAuth: !forgeConnector.isUserOAuthed,
		requiresOauth: !forgeConnector.isUserOAuthed && !forgeConnector.outboundAuthUrl ? false : true,
		icon,
		searchResultIcon,
		typeFilterConfig,
		workspaceName: forgeConnector.workspaceName,
	};

	return config;
};

export const get3pProductKeys = async ({
	cloudId,
	absoluteUrl,
	userId,
	degradationCallback,
	cacheAnalyticsCallback,
	networkCallbacks,
}: TSearch3pConfigProps) => {
	const THIRD_PARTY_TYPES: Record<string, string> = getThirdPartyTypes();

	/**
	 * These parameters define a caching strategy where:
	 *  - Cache is refreshed only if the cached data is expired, with a 1 minute TTL
	 *  - Cached value is returned, regardless of whether its expired or not
	 */
	const searchConfigParams = {
		cloudId,
		absoluteUrl,
		userId,
		degradationCallback,
		cacheAnalyticsCallback,
		expiration: 1000 * 60, // 1 minute
		returnExpired: true,
		onlyRefreshIfExpired: true,
		networkCallbacks,
	};

	const resources = await get3pSearchConfig(searchConfigParams);

	// Use a map to track connector sources for each providerId
	// This is useful for deduping search results for providers that are hybrid smartlink and full connectors
	const connectorSourceMap: ConnectorSourceMap = new Map();

	let productKeys: (ProductKeys3P | ForgeConnectorProductKey)[] = [];
	let thirdPartyConfigsWithAPIResponse: ThirdPartyConfigsBootstrap = {};

	resources.forEach((resource) => {
		// Handle Forge connectors first (if feature flag enabled)
		if (isForgeConnectorProductKey(resource.type) && fg('rovo-search-dynamic-forge-config')) {
			const config = mapForgeConnectorToThirdPartyConfig(resource);
			if (config) {
				const productKey = resource.type as ForgeConnectorProductKey;
				productKeys.push(productKey);
				thirdPartyConfigsWithAPIResponse[productKey] = config;
			}
			return; // Early return for Forge connectors
		}

		// Handle hardcoded connectors (existing logic)
		const productKey = (THIRD_PARTY_TYPES[resource.type] || '') as ProductKeys3P;
		if (!productKey) {
			return;
		}

		const { providerId, datasourceId } = resource;
		productKeys.push(productKey);
		let config = {} as ThirdPartyConfigsWithAPIResponse;
		const staticConfig = ThirdPartyConfigs.get(productKey);
		if (staticConfig) {
			const sourceType = staticConfig.isFederated
				? FEDERATED_CONNECTOR_SOURCE
				: resource.type.startsWith(LITE_CONNECTOR_SOURCE)
					? LITE_CONNECTOR_SOURCE
					: resource.type.startsWith(SMARTLINKS_CONNECTOR_SOURCE)
						? SMARTLINKS_CONNECTOR_SOURCE
						: FULL_CONNECTOR_SOURCE;

			connectorSourceMap.set(
				providerId,
				(connectorSourceMap.get(providerId) || new Set()).add(sourceType),
			);

			const existingConfig = thirdPartyConfigsWithAPIResponse[productKey];
			const isExistingConfigFullConnectorSource =
				existingConfig?.connectorSources?.includes(FULL_CONNECTOR_SOURCE) || false;

			const outboundAuthUrl =
				isExistingConfigFullConnectorSource && existingConfig?.outboundAuthUrl
					? existingConfig.outboundAuthUrl
					: resource.outboundAuthUrl;

			const userNeedsOAuth =
				isExistingConfigFullConnectorSource && existingConfig
					? existingConfig.userNeedsOAuth
					: !resource.isUserOAuthed;

			config = {
				...staticConfig,
				outboundAuthUrl,
				userNeedsOAuth,
				providerId,
				datasourceId,
				connectionId: resource.connectionId,
				workspaceName: resource.workspaceName,
				workspaceUrl: resource.workspaceUrl,
				connectorSources: Array.from(connectorSourceMap.get(providerId) || []),
				impersonationEnabled: resource.isImpersonationAuthEnabled,
			};

			if (fg('ai3p_webcrawler_multi_domain_support')) {
				const datasourceList = [];
				// Add the main config without the nested datasources
				// eslint-disable-next-line no-unused-vars
				const { datasources, ...configWithoutDatasources } = config;
				configWithoutDatasources.datasourceId = resource.datasourceId;
				configWithoutDatasources.connectionId = resource.connectionId;
				// TODO: Leave config level logic at the root of the datasource itself and datasource specific logic in the datasources -> https://hello.jira.atlassian.cloud/browse/DCCNT-1386
				const datasource = {
					...configWithoutDatasources,
					...resource,
					userNeedsOAuth: !resource.isUserOAuthed,
					integrationAri: resource.integrationId || configWithoutDatasources.integrationAri,
				};
				datasourceList.push(datasource);
				// Add third-party configs, also without the nested datasources
				const extraDatasources = thirdPartyConfigsWithAPIResponse[productKey]?.datasources || [];
				const cleanedExtraDatasources = extraDatasources.map(
					// eslint-disable-next-line no-unused-vars
					({ datasources, ...configWithoutDatasources }) => configWithoutDatasources,
				);
				datasourceList.push(...cleanedExtraDatasources);
				// Assign the cleaned datasourceList back to the main config
				config.datasources = datasourceList as ThirdPartyConfigsWithAPIResponse[];
			}
			thirdPartyConfigsWithAPIResponse[productKey] = config;
		}
	});

	// Filter product keys to only include valid ones
	// Note: Only filter hardcoded product keys, Forge connectors use their own type as product key
	const filteredProductKeys = productKeys.filter((productKey) => {
		if (isForgeConnectorProductKey(productKey)) {
			return true; // Forge connectors are always valid
		}
		// For hardcoded 3P connectors, check if they're in allProductKeys
		return allProductKeys.includes(productKey as ProductKeys3P);
	});

	return { productKeys: filteredProductKeys, thirdPartyConfigs: thirdPartyConfigsWithAPIResponse };
};

const EMPTY_RESPONSE: (ProductKeys3P | ForgeConnectorProductKey)[] = [];

export const useThirdPartyConfig = ({
	cloudId,
	absoluteUrl,
	userId,
	disabled,
	experience = SearchExperience.SEARCH_PAGE,
}: Partial<TSearch3pConfigProps> & { experience?: SearchStoreExperience } = {}) => {
	const [config, setConfig] = useState<{
		productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});
	const logException = useLogException();
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	const [searchSessionId] = useSearchSessionId();

	useEffect(() => {
		if (!config.productKeys) {
			if (!disabled && cloudId) {
				const uxTaskId = crypto.randomUUID();
				const taskStartAt = performance.now();

				const baseUXVitalStatsParams = {
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_THIRD_PARTY_CONFIG_UX,
				};

				// Fire task start for UX tracking
				fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));
				// Create network callbacks for tracking network requests
				const configNetworkTaskId = crypto.randomUUID();
				const networkCallbacks = createVitalStatsNetworkCallbacks(
					configNetworkTaskId,
					TASK_NAMES.SEARCH_PAGE_THIRD_PARTY_CONFIG_NETWORK,
					searchSessionId,
					fireAnalyticsEvent,
					taskStartAt,
					{ uxTaskId },
				);

				// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-3p-config',
							searchSessionId,
							reason,
							source: EVENT_SOURCE,
						}),
					);
				};

				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-3p-config',
							searchSessionId,
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source: EVENT_SOURCE,
						}),
					);
				};

				get3pProductKeys({
					cloudId,
					absoluteUrl,
					userId,
					degradationCallback,
					cacheAnalyticsCallback,
					networkCallbacks,
				})
					.then((data) => {
						setConfig({
							productKeys: data.productKeys,
							thirdPartyConfigs: data.thirdPartyConfigs,
						});

						// Fire user experience metric - user got data (success from UX perspective)
						fireAnalyticsEvent(
							onTaskSuccess({
								...baseUXVitalStatsParams,
								moreContext: {
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);
					})
					.catch((error) => {
						// FailedDependency is visible to the end-user. This is a health metric for our UX
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-3p-config',
								searchSessionId,
								source: EVENT_SOURCE,
							}),
						);

						// Fire user experience metric - user did not get data (failure from UX perspective)
						fireAnalyticsEvent(
							onTaskFail({
								...baseUXVitalStatsParams,
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);

						setConfig({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});

						logException(error);
					});
			} else {
				setConfig({
					productKeys: EMPTY_RESPONSE,
					thirdPartyConfigs: {},
				});
			}
		}
	}, [
		cloudId,
		absoluteUrl,
		userId,
		disabled,
		config,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
	]);

	return config;
};
